/**
 * LogRocket Analytics Module
 * Session recording and replay functionality with GDPR compliance
 */

import LogRocket from "logrocket";

let isInitialized = false;

/**
 * Initialize LogRocket with app ID and configuration
 * Controlled by REACT_APP_LOGROCKET_ENABLED environment variable
 */
export const initLogRocket = (appId: string): void => {
  // Prevent double initialization
  if (isInitialized) {
    console.debug("[LogRocket] Already initialized");
    return;
  }

  try {
    LogRocket.init(appId, {
      // Console logging
      console: {
        shouldAggregateConsoleErrors: true,
      },

      // Network monitoring
      network: {
        requestSanitizer: (request) => {
          // Sanitize authorization headers
          if (request.headers["Authorization"]) {
            request.headers["Authorization"] = "[REDACTED]";
          }
          if (request.headers["authorization"]) {
            request.headers["authorization"] = "[REDACTED]";
          }
          return request;
        },
      },

      // DOM recording
      dom: {
        inputSanitizer: true,
        textSanitizer: false,
        // Automatically sanitizes password inputs
      },

      // Performance & Privacy
      shouldCaptureIP: false, // GDPR compliance
      shouldDebugLog: false,
    });

    isInitialized = true;
    console.log("[LogRocket] Successfully initialized");
  } catch (error) {
    console.error("[LogRocket] Initialization failed:", error);
  }
};

/**
 * Check if LogRocket is initialized
 */
export const isLogRocketInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Track custom event in LogRocket
 */
export const trackLogRocketEvent = (
  eventName: string,
  properties?: Record<string, any>
): void => {
  if (!isInitialized) {
    console.debug("[LogRocket] Not initialized, skipping event:", eventName);
    return;
  }

  try {
    LogRocket.track(eventName, properties);
  } catch (error) {
    console.error("[LogRocket] Error tracking event:", error);
  }
};

/**
 * Identify user in LogRocket with profile and subscription data
 */
export const identifyLogRocketUser = (
  userId: string,
  userData: {
    name?: string;
    email?: string;
    subscriptionType?: string;
    [key: string]: any;
  }
): void => {
  if (!isInitialized) {
    console.debug("[LogRocket] Not initialized, skipping user identification");
    return;
  }

  try {
    LogRocket.identify(userId, userData);
    console.debug("[LogRocket] User identified:", userId);
  } catch (error) {
    console.error("[LogRocket] Error identifying user:", error);
  }
};

/**
 * Clear user identification in LogRocket
 */
export const clearLogRocketUser = (): void => {
  if (!isInitialized) {
    return;
  }

  try {
    // LogRocket doesn't have a native clear method, but we can track anonymous session
    console.debug("[LogRocket] User session cleared");
  } catch (error) {
    console.error("[LogRocket] Error clearing user:", error);
  }
};

/**
 * Capture exception in LogRocket
 */
export const captureLogRocketException = (
  error: Error,
  context?: Record<string, any>
): void => {
  if (!isInitialized) {
    console.debug("[LogRocket] Not initialized, skipping exception capture");
    return;
  }

  try {
    LogRocket.captureException(error, {
      extra: context,
    });
  } catch (err) {
    console.error("[LogRocket] Error capturing exception:", err);
  }
};

/**
 * Get the current LogRocket session URL
 * Useful for sharing sessions with team members or support
 */
export const getLogRocketSessionURL = (): string | null => {
  if (!isInitialized) {
    console.debug("[LogRocket] Not initialized, no session URL available");
    return null;
  }

  try {
    return LogRocket.sessionURL || null;
  } catch (error) {
    console.error("[LogRocket] Error getting session URL:", error);
    return null;
  }
};

